const LIST_KEY = "garageVehicles";
const SELECTED_KEY = "garageSelectedVehicleId";

// ✅ NEW: global event name
const GARAGE_EVENT = "garage:changed";

// ✅ NEW: notify helper
const notifyGarageChanged = () => {
  try {
    window.dispatchEvent(new Event(GARAGE_EVENT));
  } catch {
    // ignore
  }
};

// ---- validation ----
export const isValidVehicle = (v) =>
  !!(
    v &&
    String(v.make || "").trim() &&
    String(v.model || "").trim() &&
    String(v.year || "").trim()
  );

// ---- legacy (single vehicle) migration support ----
const LEGACY_KEY = "garageVehicle";

const safeJsonParse = (s, fallback) => {
  try {
    const v = JSON.parse(s);
    return v ?? fallback;
  } catch {
    return fallback;
  }
};

const genId = () => {
  // works in modern browsers; fallback for older
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return String(Date.now()) + "-" + Math.random().toString(16).slice(2);
};

export const loadGarageVehicles = () => {
  // 1) read new list
  const raw = localStorage.getItem(LIST_KEY);
  let list = safeJsonParse(raw, []);

  if (!Array.isArray(list)) list = [];

  // 2) migrate legacy single vehicle once, if present and list is empty
  if (!list.length) {
    const legacyRaw = localStorage.getItem(LEGACY_KEY);
    const legacy = safeJsonParse(legacyRaw, null);
    if (legacy && isValidVehicle(legacy)) {
      const item = { id: genId(), ...legacy, year: String(legacy.year || "").trim() };
      list = [item];
      localStorage.setItem(LIST_KEY, JSON.stringify(list));
      localStorage.setItem(SELECTED_KEY, item.id);
      localStorage.removeItem(LEGACY_KEY);

      // ✅ NEW
      notifyGarageChanged();
    }
  }

  // normalize year to string
  return list.map((v) => ({ ...v, year: String(v.year || "").trim() }));
};

export const saveGarageVehicles = (vehicles) => {
  const list = Array.isArray(vehicles) ? vehicles : [];
  localStorage.setItem(LIST_KEY, JSON.stringify(list));

  // ✅ NEW
  notifyGarageChanged();
};

export const loadSelectedGarageVehicleId = () =>
  localStorage.getItem(SELECTED_KEY) || "";

export const setSelectedGarageVehicleId = (id) => {
  if (!id) localStorage.removeItem(SELECTED_KEY);
  else localStorage.setItem(SELECTED_KEY, id);

  // ✅ NEW
  notifyGarageChanged();
};

export const loadSelectedGarageVehicle = () => {
  const list = loadGarageVehicles();
  const selectedId = loadSelectedGarageVehicleId();
  const selected = list.find((v) => v.id === selectedId);
  return selected || list[0] || null;
};

export const addGarageVehicle = (vehicle) => {
  const v = {
    make: String(vehicle.make || "").trim(),
    model: String(vehicle.model || "").trim(),
    year: String(vehicle.year || "").trim(),
    trim: String(vehicle.trim || "").trim(),
  };

  if (!isValidVehicle(v)) return null;

  const list = loadGarageVehicles();
  const item = { id: genId(), ...v };
  const next = [item, ...list]; // newest first
  localStorage.setItem(LIST_KEY, JSON.stringify(next));
  localStorage.setItem(SELECTED_KEY, item.id);

  // ✅ NEW
  notifyGarageChanged();

  return item;
};

export const updateGarageVehicle = (id, vehicle) => {
  const v = {
    make: String(vehicle.make || "").trim(),
    model: String(vehicle.model || "").trim(),
    year: String(vehicle.year || "").trim(),
    trim: String(vehicle.trim || "").trim(),
  };

  if (!id || !isValidVehicle(v)) return null;

  const list = loadGarageVehicles();
  const next = list.map((x) => (x.id === id ? { ...x, ...v } : x));
  localStorage.setItem(LIST_KEY, JSON.stringify(next));
  localStorage.setItem(SELECTED_KEY, id);

  // ✅ NEW
  notifyGarageChanged();

  return next.find((x) => x.id === id) || null;
};

export const removeGarageVehicle = (id) => {
  const list = loadGarageVehicles();
  const next = list.filter((v) => v.id !== id);
  localStorage.setItem(LIST_KEY, JSON.stringify(next));

  const selectedId = loadSelectedGarageVehicleId();
  if (selectedId === id) {
    if (next[0]) localStorage.setItem(SELECTED_KEY, next[0].id);
    else localStorage.removeItem(SELECTED_KEY);
  }

  // ✅ NEW
  notifyGarageChanged();

  return next;
};

export const clearGarageVehicles = () => {
  localStorage.removeItem(LIST_KEY);
  localStorage.removeItem(SELECTED_KEY);

  // ✅ NEW
  notifyGarageChanged();
};

// ---- OPTIONAL: keep old function names so other code won’t break ----
export const loadGarageVehicle = () => loadSelectedGarageVehicle();

export const saveGarageVehicle = (vehicle) => {
  // behaves like "save" but as a new entry (simple)
  return addGarageVehicle(vehicle);
};

export const clearGarageVehicle = () => clearGarageVehicles();
